import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes, Ref } from "react";

import { cn } from "../lib/cn";

/**
 * Every variant is expressed in token classes only — no `bg-[#97ce92]` anywhere.
 * The 44px floor is not a style choice: the design system requires it so a button
 * can be hit reliably while holding a bird.
 *
 * `rounded-control` is a full round in this design, so every button is a pill.
 */
const buttonVariants = cva(
  cn(
    "inline-flex items-center justify-center gap-sm whitespace-nowrap",
    "rounded-control text-data leading-none",
    "min-h-touch",
    "transition-colors outline-none",
    "disabled:pointer-events-none disabled:opacity-45",
  ),
  {
    variants: {
      variant: {
        /**
         * Terracotta under light text — the screen's one unmissable action, the same
         * colour as the log button. Never `bg-secondary`, which is a text value.
         */
        primary: "bg-action text-on-action hover:opacity-90",
        /** Ink. For an action that is definite but not the screen's headline. */
        ink: "bg-on-surface text-surface hover:bg-inverse-surface",
        /** Pale sage. A definite action that is not the screen's headline. */
        sage: "bg-primary-fixed text-on-primary-fixed hover:bg-primary-fixed-dim",
        /** Deep green. Links-as-buttons and anything secondary to an accent CTA. */
        brand: "bg-primary text-on-primary hover:bg-primary-container",
        /** A raised neutral pill on the page ground. */
        secondary: cn(
          "bg-surface-container-lowest text-on-surface shadow-card",
          "hover:bg-surface-container-low",
        ),
        ghost: "text-on-surface hover:bg-surface-container",
        danger: "bg-error text-on-error hover:opacity-90",
      },
      size: {
        // Still at least 44px tall; the padding and height are what change.
        sm: "px-md",
        md: "px-lg",
        /** A form's own commit button: 48px, still the data size. */
        wide: "h-12 px-lg",
        /** The floating action that ends a wizard step, and the sign-in submit. */
        lg: "h-14 px-xl text-cta",
        icon: "w-touch px-0",
      },
      block: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
      block: false,
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  ref?: Ref<HTMLButtonElement>;
}

export function Button({ className, variant, size, block, ref, ...props }: ButtonProps) {
  return (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size, block }), className)}
      {...props}
    />
  );
}

export { buttonVariants };
